/**
 * Two halves of the same thing, per entity operation.
 *
 * `requests` says how the client invokes an operation: which endpoint it posts
 * to and what body it sends. `<operation>: [ …steps ]` says what the local
 * document looks like the moment the worker taps, before the round trip lands —
 * and the server applies the same steps authoritatively through its
 * `applyOptimistic` rule step, which is why this file lives in common/.
 *
 * The request bodies are interpolated by `resolveParams`:
 *
 *   ${item.x}    a field on the work item
 *   ${record.x}  a field on the record that holds it
 *   ${input.x}   what the section supplied — the quantity and location the
 *                worker chose, or the item's own when the card acts directly
 *   ${uuid.name} a fresh id, the same value everywhere that name appears in
 *                one call, and different for a different name
 *
 * An array element carrying `when` is dropped when that resolves falsy, which is
 * how a reservation list stays empty for a line that never had one.
 */
const optimisticLogicData = {

    warehouseOrder: {
        linesField:          'lines',
        totalLinesField:     'totalLines',
        completedLinesField: 'completedLines',

        requests: {
            // Picking and putting away are both `reserve` with a different verb
            // in the body; what differs is where the reservation is held. A pick
            // reserves at the location the stock is taken from, a putaway at the
            // receiving location, so nobody else claims the goods while the
            // worker is walking them to their destination.
            pick: {
                endpoint: 'reserve',
                body: {
                    lineId: '${item.id}', itemId: '${item.itemId}',
                    qty: '${input.qty}', locationId: '${input.location}',
                    newReservationId: '${uuid.reservation}',
                    reservations: [{
                        id: '${uuid.reservation}', lineId: '${item.id}',
                        itemId: '${item.itemId}', itemName: '${item.itemName}',
                        unitId: '${item.unitId}', unitName: '${item.unitName}',
                        locationId: '${input.location}', quantity: '${input.qty}',
                    }],
                },
            },
            putaway: {
                endpoint: 'reserve',
                body: {
                    lineId: '${item.id}', itemId: '${item.itemId}',
                    qty: '${input.qty}', locationId: '${input.location}',
                    newReservationId: '${uuid.reservation}',
                    reservations: [{
                        id: '${uuid.reservation}', lineId: '${item.id}',
                        itemId: '${item.itemId}', itemName: '${item.itemName}',
                        unitId: '${item.unitId}', unitName: '${item.unitName}',
                        locationId: '${item.fromLocationId}', quantity: '${input.qty}',
                    }],
                },
            },

            // Taking less than planned splits the line: this half is done, the
            // remainder becomes a new one. remainingQty and newLineId are what
            // the line.insert step reads.
            splitAndPick: {
                endpoint: 'reserve',
                body: {
                    lineId: '${item.id}', itemId: '${item.itemId}',
                    qty: '${input.qty}', locationId: '${input.location}',
                    newReservationId: '${uuid.reservation}',
                    remainingQty: '${input.remainingQty}', newLineId: '${uuid.line}',
                    reservations: [{
                        id: '${uuid.reservation}', lineId: '${item.id}',
                        itemId: '${item.itemId}', itemName: '${item.itemName}',
                        unitId: '${item.unitId}', unitName: '${item.unitName}',
                        locationId: '${input.location}', quantity: '${input.qty}',
                    }],
                },
            },
            splitAndPutaway: {
                endpoint: 'reserve',
                body: {
                    lineId: '${item.id}', itemId: '${item.itemId}',
                    qty: '${input.qty}', locationId: '${input.location}',
                    newReservationId: '${uuid.reservation}',
                    remainingQty: '${input.remainingQty}', newLineId: '${uuid.line}',
                    reservations: [{
                        id: '${uuid.reservation}', lineId: '${item.id}',
                        itemId: '${item.itemId}', itemName: '${item.itemName}',
                        unitId: '${item.unitId}', unitName: '${item.unitName}',
                        locationId: '${item.fromLocationId}', quantity: '${input.qty}',
                    }],
                },
            },

            // Reversing one: release the reservation it created, if it had one.
            unpick: {
                endpoint: 'unreserve',
                body: {
                    lineId: '${item.id}', itemId: '${item.itemId}',
                    reservations: [{ when: '${item.reservationId}', reservationId: '${item.reservationId}' }],
                },
            },
            unputaway: {
                endpoint: 'unreserve',
                body: {
                    lineId: '${item.id}', itemId: '${item.itemId}',
                    reservations: [{ when: '${item.reservationId}', reservationId: '${item.reservationId}' }],
                },
            },

            // Drop stops act on a whole destination rather than one line, so the
            // work item here is the consolidated stop.
            dropLocation:   { endpoint: 'dropLocation',   body: { toLocationId: '${item.toLocationId}' } },
            undropLocation: { endpoint: 'undropLocation', body: { toLocationId: '${item.toLocationId}' } },

            // Reversing a whole stop runs the proven per-line unreserve once per
            // line, rather than a bespoke group endpoint that would have to get
            // reservation release right a second time.
            unpickLocation: {
                forEach: {
                    operation: 'unpick',
                    where: { toLocationId: '${item.toLocationId}', done: true },
                },
            },
            drop:           { endpoint: 'drop',           body: { lineId: '${item.id}' } },
            undrop:         { endpoint: 'undrop',         body: { lineId: '${item.id}' } },

            // Whole-document operations: the body is the record itself, there is
            // nothing to patch locally, and the view reloads from the response.
            complete: { endpoint: 'complete', document: true },
            split:    { endpoint: 'split',    document: true },
        },

        pick: [
            {
                type: 'line.set',
                set:  { done: true, actualQuantity: '#qty', fromLocationId: '#locationId', reservationId: '#newReservationId' },
            },
        ],

        unpick: [
            {
                type: 'line.set',
                set:  { done: false, actualQuantity: null, reservationId: null },
            },
        ],

        splitAndPick: [
            {
                type: 'line.set',
                set:  { done: true, quantity: '#qty', actualQuantity: '#qty', fromLocationId: '#locationId', reservationId: '#newReservationId' },
            },
            {
                type: 'line.insert',
                set:  { done: false, quantity: '#remainingQty', actualQuantity: null, fromLocationId: null, reservationId: null },
            },
        ],

        // Transfer drop leg: progress-only, no inventory. Marks the line placed
        // at its destination; actual stock moves at order completion.
        drop: [
            {
                type: 'line.set',
                set:  { dropped: true },
            },
        ],

        undrop: [
            {
                type: 'line.set',
                set:  { dropped: false },
            },
        ],

        // Group drop: mark every picked (done) line at one destination dropped in
        // a single action — the route's drop stop consolidates lines by
        // toLocationId, so the worker drops a whole stop at once.
        dropLocation: [
            {
                type: 'line.setWhere',
                match: { toLocationId: '#toLocationId', done: true },
                set:   { dropped: true },
            },
        ],

        undropLocation: [
            {
                type: 'line.setWhere',
                match: { toLocationId: '#toLocationId' },
                set:   { dropped: false },
            },
        ],

        putaway: [
            {
                type: 'line.set',
                set:  { done: true, actualQuantity: '#qty', toLocationId: '#locationId', reservationId: '#newReservationId' },
            },
        ],

        unputaway: [
            {
                type: 'line.set',
                set:  { done: false, actualQuantity: null, toLocationId: null, reservationId: null },
            },
        ],

        splitAndPutaway: [
            {
                type: 'line.set',
                set:  { done: true, quantity: '#qty', actualQuantity: '#qty', toLocationId: '#locationId', reservationId: '#newReservationId' },
            },
            {
                type: 'line.insert',
                set:  { done: false, quantity: '#remainingQty', actualQuantity: null, toLocationId: null, reservationId: null },
            },
        ],
    },

}

export default optimisticLogicData
